import React from 'react'
import { View } from 'react-native'
import { useDispatch, useSelector } from 'react-redux'
import { useTheme } from '@ui-kitten/components'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Text, Button } from '@components/base'
import { BottomSheet } from '@components'
import { useGoBack } from '@src/utils/navigation'
import { requestInAppReview } from '@utils/appReview'
import { userSelector } from '@selectors/app.ts'
import { useIsDarkMode } from '@config/theme.ts'
import { UserPropertyKind } from '@src/types.ts'
import { Analytics, CustomEventTypes } from '@config'
import { CoachAvatar } from '@src/components/Avatar/CoachAvatar'

export const PositiveCoachRatingRateAppModal = () => {
  const styles = useStyleSheet(themedStyles)
  const user = useSelector(userSelector)
  const coach = user?.lastOccurredCoachingAppointment?.providers[0]?.coach
  const theme = useTheme()
  const isDarkMode = useIsDarkMode()
  const dispatch = useDispatch()

  const goBack = useGoBack()

  const onRateButtonPress = () => {
    requestInAppReview()
    dispatch({
      type: 'users/upsertUserProperty',
      payload: {
        kind: UserPropertyKind.CoachRatingInitiatedRatingStatus,
        value: 'rated',
      },
    })
    Analytics.track(CustomEventTypes.AppRatingOpened, {
      userId: user?.id,
    })
    goBack()
  }

  const onDismiss = () => {
    dispatch({
      type: 'users/upsertUserProperty',
      payload: {
        kind: UserPropertyKind.CoachRatingInitiatedRatingStatus,
        value: 'dismissed',
      },
    })
    Analytics.track(CustomEventTypes.AppRatingSkipped, {
      userId: user?.id,
    })
    goBack()
  }

  const photo = coach?.photo
  const initials = coach?.fullName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()

  return (
    <BottomSheet>
      <View style={styles.container}>
        <View style={styles.bubbleContainer}>
          <Text type="regular" style={styles.bubbleText}>
            It was great chatting with you. Thanks for the 5 stars!
          </Text>
          <View style={styles.halfBubble} />
          <View style={styles.borderCover} />
          <View style={styles.smallBubble} />
        </View>
        <CoachAvatar photo={photo} style={styles.coachPhoto} />
        {!photo && (
          <View
            style={[
              styles.initialsContainer,
              {
                backgroundColor: isDarkMode
                  ? theme['theme.accent.brick']
                  : theme['theme.accent.citron'],
              },
            ]}
          >
            <Text type="large" bold style={styles.initials}>
              {initials}
            </Text>
          </View>
        )}
        <Text type="title-2" style={styles.title}>
          Would you like to rate the Nutrisense app too?
        </Text>
        <Button
          type="primary"
          size="block"
          style={styles.confirmButton}
          onPress={onRateButtonPress}
          accessibilityLabel="rateButton"
        >
          Rate
        </Button>
        <Button
          type="transparent"
          size="block"
          accessibilityLabel="Dismiss Button"
          onPress={onDismiss}
        >
          Dismiss
        </Button>
      </View>
    </BottomSheet>
  )
}

const themedStyles = StyleService.create({
  container: {
    marginTop: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  bubbleContainer: {
    marginBottom: -35,
    zIndex: 1,
  },
  bubbleText: {
    textAlign: 'center',
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'theme.surface.base1',
    width: 250,
    backgroundColor: 'theme.background',
  },
  halfBubble: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'theme.surface.base1',
    backgroundColor: 'theme.background',
    width: 20,
    height: 20,
    position: 'relative',
    top: -10,
    left: 85,
    zIndex: -1,
  },
  borderCover: {
    borderWidth: 1,
    borderColor: 'theme.background',
    width: 18,
    position: 'relative',
    top: -21,
    left: 86,
  },
  smallBubble: {
    borderRadius: 4,
    borderWidth: 1,
    borderColor: 'theme.surface.base1',
    backgroundColor: 'theme.background',
    width: 8,
    height: 8,
    position: 'relative',
    top: -12,
    left: 100,
  },
  title: {
    textAlign: 'center',
    marginBottom: 40,
    marginHorizontal: 32,
  },
  confirmButton: {
    marginBottom: 8,
  },
  coachPhoto: {
    width: 80,
    height: 80,
    borderRadius: 48,
    marginBottom: 16,
    zIndex: -20,
  },
  spinner: {
    color: 'theme.surface.base',
  },
  initialsContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    marginBottom: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  initials: {
    color: 'theme.primary.dark',
  },
})
